"use client"

import styled from "@emotion/styled"
import { Box, IconButton } from "@mui/material"
import { useRef, useState } from "react"
import { extractProperty, getUmbracoMediaUrl, replaceRichTextLinks } from "utils"
import { isVideo, ResponsiveMedia } from "./common/bit/MediaRenderer"
import { PauseIcon, Play2Icon } from "./icons"
import PortContainer from "./PortContainer"
import PortPrimaryButton from "./PortPrimaryButton"

const StyledSection = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  color: white;
`

const StyledMedia = styled.div`
  display: flex;
  width: 100%;
  height: 500px;
  position: relative;
  top: 0;
  left: 0;
  & > div {
    width: 100%;
    height: 100%;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
  }
  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ControlButton = styled(IconButton)<{ $isPlaying: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  padding: 0;
  background-color: ${({ theme }) => theme.colors.port.defaultTextColor};
  transition: all 0.3s ease-out;
  color: white;
  & svg {
    color: inherit;
  }
  &:hover {
    background-color: rgb(255, 255, 255);
    color: ${({ theme }) => theme.colors.port.defaultTextColor};
  }
`

const StyledContent = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.port.defaultTextColor};
`

const StyledTitle = styled.h1`
  margin: 0;
  font-size: 40px;
  font-weight: 400;
  line-height: 125%; /* 37.5px */
  letter-spacing: -0.6px;
  text-transform: capitalize;
  margin-bottom: 8px;
  ${(props) => props.theme.breakpoints.up("md")} {
    letter-spacing: -0.8px;
  }
`
const StyledSubtitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 300;
  line-height: 150%; /* 30px */
  letter-spacing: 0.6px;
  text-transform: capitalize;
  white-space: pre-line;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px;
    letter-spacing: unset;
  }
`
type PortInANutshellProps = {
  data: any
}

const PortInANutshell = ({ data }: PortInANutshellProps) => {
  const id = extractProperty(data, "id") || "port-in-a-nutshell"
  const desktopMedia = extractProperty(data, "desktopMedia")
  const mobileMedia = extractProperty(data, "mobileMedia")
  const title = extractProperty(data, "title")
  const subtitle = extractProperty(data, "subtitle")
  const button: {
    name?: string | null
    target?: string | null
    url?: string
    type?: "EXTERNAL" | "INTERNAL" | null
  } = extractProperty(data, "button")?.[0]

  const desktopVideoRef = useRef<HTMLVideoElement>(null)
  const mobileVideoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(true)
  const hasVideoMedia = isVideo(desktopMedia) || isVideo(mobileMedia)

  const altText = typeof title === "string" && title.length ? title : "Port In A Nutshell MEDIA"

  const syncPlaybackState = (shouldPlay: boolean) => {
    // eslint-disable-next-line no-extra-semi
    ;[desktopVideoRef.current, mobileVideoRef.current].forEach((video) => {
      if (!video) return
      if (shouldPlay) {
        const playPromise = video.play()
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => undefined)
        }
      } else {
        video.pause()
      }
    })
  }

  const handlePlaybackToggle = () => {
    setIsPlaying((prev) => {
      const next = !prev
      syncPlaybackState(next)
      return next
    })
  }

  // in case data fields are empty, return null
  if (!desktopMedia && !mobileMedia && !title && !subtitle) {
    return null
  }

  return (
    <StyledSection id={id}>
      <StyledMedia>
        <ResponsiveMedia
          desktopMedia={desktopMedia ? getUmbracoMediaUrl(desktopMedia) : undefined}
          mobileMedia={mobileMedia ? getUmbracoMediaUrl(mobileMedia) : undefined}
          alt={altText}
          priority
          desktopVideoRef={desktopVideoRef}
          mobileVideoRef={mobileVideoRef}
        />
        {hasVideoMedia && (
          <Box
            component={"span"}
            sx={{
              position: { xs: "absolute" },
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 2,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <PortContainer
              paddingInline={20}
              maxWidthXL={1440}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                paddingBlock: {
                  xs: "40px",
                },
              }}
            >
              <ControlButton
                aria-label={isPlaying ? "Pause background video" : "Play background video"}
                onClick={handlePlaybackToggle}
                disableRipple
                $isPlaying={isPlaying}
              >
                {isPlaying ? <PauseIcon /> : <Play2Icon />}
              </ControlButton>
            </PortContainer>
          </Box>
        )}
      </StyledMedia>
      <StyledContent>
        <PortContainer
          paddingInline={20}
          maxWidthXL={1440}
          sx={{
            paddingTop: {
              xs: "24px",
              md: "32px",
            },
            paddingBottom: {
              xs: "60px",
              md: "90px",
            },
            display: "flex",
            gap: { xs: "16px", md: "40px" },
            flexDirection: {
              xs: "column",
            },
            alignItems: {
              xs: "flex-start",
            },
          }}
        >
          <Box sx={{ width: "100%" }}>
            {title && <StyledTitle>{title}</StyledTitle>}
            {subtitle && (
              <StyledSubtitle
                dangerouslySetInnerHTML={{
                  __html: replaceRichTextLinks(subtitle).replace(/\n/g, "<br>"),
                }}
              />
            )}
          </Box>
          <PortPrimaryButton button={button} />
        </PortContainer>
      </StyledContent>
    </StyledSection>
  )
}
export default PortInANutshell
